import React, { useEffect, useState } from 'react';
import { useVibrancy } from '../VibrancyDashboardContext';
import { VibrancyProfileService } from '../vibrancyProfileService';
import { BarRow, HeaderIcon, cardHeaderStyle, cardStyle } from './shared';

type Period = 'monthly' | 'quarterly' | 'halfyearly' | 'annual';

// ─── StatTile (unchanged) ─────────────────────────────────────────────────────

const StatTile: React.FC<{
    value: string; label: string; bg: string; color: string;
    compare?: { prev: string; delta: string } | null;
}> = ({ value, label, bg, color, compare }) => (
    <div className="rounded-3 text-center py-2 h-100" style={{ background: bg }}>
        <div className="fw-bold" style={{ fontSize: 22, color }}>{value}</div>
        <div className="text-uppercase fw-medium font-11 text-secondary mt-1">{label}</div>
        {compare && (
            <div className="mt-1" style={{ fontSize: 10.5 }}>
                <span className="fw-bold" style={{ color: '#4a5a80' }}>{compare.prev}</span>
                <span className="fw-bold ms-1" style={{ color: '#0f9d58' }}>{compare.delta}</span>
            </div>
        )}
    </div>
);

const SkeletonSummary: React.FC = () => (
    <div className="placeholder-glow">
        <div className="row g-2 mb-3">
            {[1,2,3,4].map(i => (
                <div key={i} className="col-3">
                    <div className="rounded-3 py-2 px-2" style={{ background: '#f4f6fb' }}>
                        <span className="placeholder col-8 d-block mx-auto mb-1" style={{ height: 22 }} />
                        <span className="placeholder col-10 d-block mx-auto" style={{ height: 10 }} />
                    </div>
                </div>
            ))}
        </div>
        <span className="placeholder col-12 d-block rounded mb-2" style={{ height: 10 }} />
        {[1,2,3,4].map(i => <span key={i} className="placeholder col-12 d-block rounded mb-2" style={{ height: 14 }} />)}
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

/** VD-10 Outreach Summary (live) + Channels Used + Commitments (context). */
const SkeletonCommitments: React.FC = () => (
    <div className="placeholder-glow">
        <div className="row g-3 mb-3">
            {[1,2,3].map(i => (
                <div key={i} className="col-md-4">
                    <div className="border rounded-3 p-3">
                        <span className="placeholder col-6 d-block mb-3" style={{ height: 16 }} />
                        <div className="row g-2">
                            {[1,2,3].map(j => <div key={j} className="col-4"><span className="placeholder col-12 d-block rounded-3" style={{ height: 60 }} /></div>)}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const OutreachStats: React.FC<{ period?: Period }> = ({ period }) => {
    const { data, timeframe: globalTimeframe, selectedInstituteId, instituteLoaded } = useVibrancy();
    const timeframe = period ?? globalTimeframe;   // section period overrides the global one

    const [summary, setSummary] = useState<any>(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    const [commitments, setCommitments] = useState<any>(null);
    const [loadingCommitments, setLoadingCommitments] = useState(false);

    useEffect(() => {
        if (!selectedInstituteId) { setSummary(null); return; }
        setLoadingSummary(true);
        VibrancyProfileService.getOutreachSummary(selectedInstituteId, timeframe)
            .then(r  => setSummary(r.data?.data || null))
            .catch(() => setSummary(null))
            .finally(() => setLoadingSummary(false));
    }, [selectedInstituteId, timeframe]);

    useEffect(() => {
        if (!selectedInstituteId) { setCommitments(null); return; }
        setLoadingCommitments(true);
        VibrancyProfileService.getCommitmentsData(selectedInstituteId, timeframe)
            .then(r  => setCommitments(r.data?.data || null))
            .catch(() => setCommitments(null))
            .finally(() => setLoadingCommitments(false));
    }, [selectedInstituteId, timeframe]);

    if (!instituteLoaded || !data) return null;

    const channels = summary?.channels ?? [];
    const focused    = commitments?.focused    ?? [];
    const notInFocus = commitments?.notInFocus ?? [];
    const showConversion = timeframe !== 'monthly';
    const focusedCol = focused.length >= 3 ? 'col-md-4'
                     : focused.length === 2 ? 'col-md-6' : 'col-md-12';

    const d = summary?.disposition;

    return (
        <>
            {/* <SectionDivider icon="📞" title="Outreach Stats & Metrics"
                note="univariety team outreach to alumni — client-safe view (no internal team breakdown)" /> */}

            <div className="row g-3 mb-3">
                {/* ── Outreach Summary (live data) ─────────────────────────── */}
                <div className="col-md-6">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
                            <div className="fw-medium d-flex align-items-center gap-2 font-14">
                                <HeaderIcon icon="📞" bg="rgba(10,31,68,.1)" color="#0a1f44" />Outreach Summary
                            </div>
                            <span className="font-13 text-secondary">
                                {data.periodLabel} · {data.institute.name.split(' ')[0]}
                            </span>
                        </div>
                        <div className="card-body">
                            {loadingSummary || !summary ? (
                                <SkeletonSummary />
                            ) : (
                                <>
                                    {/* 4 stat tiles */}
                                    <div className="row g-2 mb-3">
                                        <div className="col-3">
                                            <StatTile value={String(summary.totalAttempts)} label="Total Attempts" bg="#f4f6fb" color="#0a1f44" />
                                        </div>
                                        <div className="col-3">
                                            <StatTile value={`${summary.coveragePct}%`} label="Coverage" bg="#ebf2ff" color="#1b52c4" />
                                        </div>
                                        <div className="col-3">
                                            <StatTile value={String(summary.successful)} label="Successful" bg="#d1fae5" color="#0f9d58" />
                                        </div>
                                        <div className="col-3">
                                            <StatTile value={String(summary.followUp)} label="Follow-up" bg="#fef3c7" color="#f59e0b" />
                                        </div>
                                    </div>

                                    {/* Disposition bar */}
                                    <div className="text-uppercase fw-bold mb-1"
                                        style={{ fontSize: 10, color: '#8fa0c0', letterSpacing: .5 }}>
                                        Outcome Breakdown
                                    </div>
                                    <div className="d-flex rounded overflow-hidden mb-2 mt-2"
                                        style={{ height: 10, gap: 2 }}>
                                        {d.successfulPct > 0 && <div style={{ width: `${d.successfulPct}%`, background: '#0f9d58', borderRadius: 3 }} />}
                                        {d.followUpPct   > 0 && <div style={{ width: `${d.followUpPct}%`,   background: '#f59e0b', borderRadius: 3 }} />}
                                        {d.noRespPct     > 0 && <div style={{ width: `${d.noRespPct}%`,     background: '#9ca3af', borderRadius: 3 }} />}
                                    </div>
                                    <div className="d-flex gap-3 mb-3 font-12 text-secondary">
                                        <span>Successful · {summary.successful}</span>
                                        <span>Follow-up · {summary.followUp}</span>
                                        <span>No Response · {summary.closed}</span>
                                    </div>

                                    {/* Top purposes */}
                                    {summary.purposes.map((p: any, i: number) => (
                                        <BarRow key={i} label={p.category} right={p.count}
                                            color={p.color} width={p.width} />
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Outreach Channels (dummy — unchanged) ─────────────────── */}
                <div className="col-md-6">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-header bg-white" style={cardHeaderStyle}>
                            <div className="fw-medium d-flex align-items-center gap-2 font-14">
                                <HeaderIcon icon="📡" bg="#cffafe" color="#0891b2" />Outreach Channels Used
                            </div>
                        </div>
                        <div className="card-body">
                            {channels.map((c: any, i: number) => (
                                <BarRow key={i} dot={false} label={c.label} color={c.color} width={c.width}
                                    right={<span className="font-12 fw-medium text-secondary text-opacity-75">{c.meta}</span>} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Commitments Captured (live) ───────────────────────────────── */}
            <div className="card mb-3" style={cardStyle}>
                <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap" style={cardHeaderStyle}>
                    <div className="fw-medium d-flex align-items-center gap-2 font-14">
                        <HeaderIcon icon="🤝" bg="#fce7f3" color="#db2777" />Commitments Captured
                    </div>
                    {!loadingCommitments && commitments && (
                        <div className="d-flex align-items-center gap-2">
                            <span className="font-13 text-secondary">
                                {commitments.thisPeriod} this period · {commitments.ytd} YTD
                            </span>
                            <span className="badge fw-bold" style={{ fontSize: 10, background: '#d1fae5', color: '#065f46' }}>
                                {commitments.conversionRate}% conversion rate
                            </span>
                        </div>
                    )}
                </div>
                <div className="card-body">
                    {loadingCommitments ? (
                        <SkeletonCommitments />
                    ) : (
                        <>
                            {focused.length > 0 && (
                                <div className="row g-3 mb-3">
                                    {focused.map((f: any, i: number) => (
                                        <div key={i} className={focusedCol}>
                                            <div className="border rounded-3 p-3 h-100"
                                                style={{ borderColor: '#e1e8f5', borderTop: '3px solid #f97316', boxShadow: '0 2px 10px rgba(249,115,22,.08)' }}>
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <div className="fw-medium d-flex align-items-center gap-2 font-14">{f.icon} {f.name}</div>
                                                    <span className="badge rounded-pill fw-bold border"
                                                        style={{ fontSize: 9, background: '#fff7ed', color: '#9a3412', borderColor: '#fed7aa' }}>
                                                        ⭐ In Focus
                                                    </span>
                                                </div>
                                                <div className="row g-2">
                                                    <div className="col-4"><div className="text-center rounded-3 py-2 bg-primary bg-opacity-10"><div className="fw-bold" style={{ fontSize: 24, color: '#0a1f44' }}>{f.total}</div><div className="text-uppercase fw-bold mt-1" style={{ fontSize: 9, color: '#8fa0c0' }}>Total</div></div></div>
                                                    <div className="col-4"><div className="text-center rounded-3 py-2 bg-success bg-opacity-10"><div className="fw-bold" style={{ fontSize: 24, color: '#0f9d58' }}>{f.confirmed}</div><div className="text-uppercase fw-bold mt-1" style={{ fontSize: 9, color: '#065f46' }}>Confirmed</div></div></div>
                                                    <div className="col-4"><div className="text-center rounded-3 py-2 bg-warning bg-opacity-10"><div className="fw-bold" style={{ fontSize: 24, color: '#f59e0b' }}>{f.followUp}</div><div className="text-uppercase fw-bold mt-1" style={{ fontSize: 9, color: '#92400e' }}>Follow-up</div></div></div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {notInFocus.length > 0 && (
                                <div className="d-flex align-items-center gap-2 flex-wrap pt-3 border-top"
                                    style={{ borderTopColor: '#e1e8f5', borderTopStyle: 'dashed' }}>
                                    <span className="text-uppercase fw-bold" style={{ fontSize: 10, color: '#8fa0c0', letterSpacing: .4 }}>Not in focus:</span>
                                    {notInFocus.map((p: any, i: number) => (
                                        <div key={i} className="d-flex align-items-center gap-1 px-3 py-1 rounded-pill border"
                                            style={{ background: '#f4f6fb', borderColor: '#e1e8f5' }}>
                                            <span style={{ fontSize: 13 }}>{p.icon}</span>
                                            <span className="fw-medium" style={{ fontSize: 11.5, color: '#8fa0c0' }}>{p.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {focused.length === 0 && notInFocus.length === 0 && (
                                <div className="text-center text-secondary font-13 py-3">No commitment data for this period</div>
                            )}
                            {showConversion && commitments && (
                                <div className="pt-3 mt-2 border-top d-flex justify-content-between align-items-center"
                                    style={{ borderTopColor: '#e1e8f5' }}>
                                    <span className="text-uppercase fw-bold" style={{ fontSize: 10, color: '#8fa0c0' }}>Conversion Rate (YTD)</span>
                                    <span className="fw-bold" style={{ color: '#0f9d58' }}>{commitments.conversionRate}%</span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default OutreachStats;
